import React, { useState } from 'react';
import './MarketplacePracticeLocation.scss';
import { Input } from '@availity/form';
import { FormGroup } from 'reactstrap';
import { EntityLocationInfo } from '../../../../slices/profiles-data-slice/models/entity-location-info';
import {
    LocationType,
    LocationTypeOptions,
} from '../../../../slices/marketplace/timeline/models/enums/location-type.enum';
import Helpers from '../../../../utils/helper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import ConfirmationModal from '../../../Modal/ConfirmationModal';
import StateSelectField from '../../../FormFields/StateSelectField/StateSelectField';
import { getIn, useFormikContext } from 'formik';
import { useServiceFocuses } from '../../../../custom-hooks/useServiceFocuses';
import { isOtherFocus } from '../../../../containers/UserPages/Login/Modals/PracticeRegistrationForm/utils';
import { NotificationFlag } from '../../../NotificationFlag/NotificationFlag';
import { NotificationFlagsCategories } from '../../../../models/NotificationFlags/NotificationFlagsCategories';
import { StarRating } from '../../../StarRating/StarRating';

interface MarketplacePracticeLocationProps {
    location: EntityLocationInfo;
    index: number;
    fieldPrefix: string;
    setFormFieldValue(name: string, value: any): void;
    removeLocation(locationIndex: number): void;
    isEditable?: boolean;
    canEditRatings?: boolean;
    showReadonlyRatings?: boolean;
    isOtherPracticeFocusColumnVisible?: boolean;
    timelineId?: number;
    practiceId?: string;
}

const RATING_PLATFORMS = [
    { key: 'google', label: 'Google' },
    { key: 'facebook', label: 'Facebook' },
    { key: 'yelp', label: 'Yelp' },
];

const MarketplacePracticeLocation = (
    props: MarketplacePracticeLocationProps
) => {
    const { isEditable = true, canEditRatings = false, showReadonlyRatings = false, timelineId, practiceId } = props;
    const serviceFocuses = useServiceFocuses();
    const location = props.location;
    const [
        isDeleteConfirmationPopupVisible,
        setIsDeleteConfirmationPopupVisible,
    ] = useState(false);
    const { values, errors } = useFormikContext();

    const ratingsFieldPrefix = `${props.fieldPrefix}[${props.index}].ratings`;

    const formatRatingDisplay = (v: unknown): string => {
        if (v === '' || v == null) return '';
        const n = Number(v);
        if (isNaN(n)) return '';
        const clamped = Math.min(5, Math.max(0, n));
        return clamped % 1 === 0 ? clamped.toString() : clamped.toFixed(1);
    };

    const isOtherPracticeFocusInputVisible = (fieldPath: string) => {
        const selectedFocus = getIn(values, fieldPath);
        return isOtherFocus(serviceFocuses, selectedFocus);
    };
    const handleLocationRemoval = () => {
        props.removeLocation(props.index);
    };

    const locationFieldBadge = (field: string, includePrefix?: boolean) =>
        timelineId && practiceId ? (
            <NotificationFlag
                flagClassName="notification-flag-wrapper"
                doNotRemove={true}
                includePrefix={includePrefix}
                eventCode={`${NotificationFlagsCategories.MpCode}.${timelineId}.${NotificationFlagsCategories.SellerProfileUpdatedCode}.PracticeProfiles.${practiceId}.PracticeLocations.${location.id}.${field}`}
            />
        ) : null;

    const locationRowBadge = () =>
        timelineId && practiceId ? (
            <NotificationFlag
                flagClassName="notification-flag-wrapper"
                doNotRemove={true}
                includePrefix={true}
                eventCode={`${NotificationFlagsCategories.MpCode}.${timelineId}.${NotificationFlagsCategories.SellerProfileUpdatedCode}.PracticeProfiles.${practiceId}.PracticeLocations.${location.id}`}
            />
        ) : null;

    return (
        <>
            <tr className="practice-location">
                <td>
                    <div className="cell-with-badge">
                        <FormGroup>
                            <Input
                                name={`${props.fieldPrefix}[${props.index}].dbaName`}
                                type="text"
                                disabled={!isEditable}
                                className="form-control"
                            />
                        </FormGroup>
                        {locationFieldBadge('DbaName')}
                        {locationRowBadge()}
                    </div>
                </td>
                <td>
                    <div className="cell-with-badge">
                        <FormGroup>
                            <Input
                                name={`${props.fieldPrefix}[${props.index}].addressInfo.streetAddress`}
                                type="text"
                                className="form-control"
                                disabled={!isEditable}
                            />
                        </FormGroup>
                        {locationFieldBadge('AddressInfo.StreetAddress')}
                    </div>
                </td>
                <td>
                    <div className="cell-with-badge">
                        <FormGroup>
                            <Input
                                name={`${props.fieldPrefix}[${props.index}].addressInfo.city`}
                                type="text"
                                className="form-control"
                                disabled={!isEditable}
                            />
                        </FormGroup>
                        {locationFieldBadge('AddressInfo.City')}
                    </div>
                </td>
                <td>
                    <div className="cell-with-badge">
                        <StateSelectField
                            name={`${props.fieldPrefix}[${props.index}].addressInfo.state`}
                            disabled={!isEditable}
                            className=""
                            label=""
                            placeholder="Select State"
                        />
                        {locationFieldBadge('AddressInfo.State')}
                    </div>
                </td>
                <td>
                    <div className="cell-with-badge">
                        <FormGroup>
                            <Input
                                name={`${props.fieldPrefix}[${props.index}].addressInfo.zipCode`}
                                type="text"
                                className="form-control"
                                disabled={!isEditable}
                                maxLength={5}
                                minLength={5}
                                pattern="[0-9]{5}"
                            />
                        </FormGroup>
                        {locationFieldBadge('AddressInfo.ZipCode')}
                    </div>
                </td>
                <td>
                    <div className="cell-with-badge">
                        <FormGroup>
                            <Input
                                name={`${props.fieldPrefix}[${props.index}].locationType`}
                                type="select"
                                disabled={!isEditable}
                                className="form-control"
                                defaultValue={LocationType.Urban}>
                                {Object.entries(LocationTypeOptions).map(
                                    ([key, value]) => (
                                        <option key={key} value={key}>
                                            {value}
                                        </option>
                                    )
                                )}
                            </Input>
                        </FormGroup>
                        {locationFieldBadge('LocationType')}
                    </div>
                </td>
                <td>
                    <div className="cell-with-badge">
                        <FormGroup>
                            <Input
                                name={`${props.fieldPrefix}[${props.index}].fteDvms`}
                                type="select"
                                className="form-control"
                                disabled={!isEditable}
                                defaultValue={0.5}
                                onChange={(event) => {
                                    props.setFormFieldValue(
                                        `${props.fieldPrefix}[${props.index}].fteDvms`,
                                        event.target.value
                                    );
                                }}>
                                {Helpers.getFteDvmsNumberList().map((option) => (
                                    <option key={option.id} value={option.id}>
                                        {option.value}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>
                        {locationFieldBadge('FteDvms')}
                    </div>
                </td>
                <td className="practice-focus">
                    <div className="cell-with-badge">
                        <FormGroup>
                            <Input
                                name={`${props.fieldPrefix}[${props.index}].practiceFocusType`}
                                type="select"
                                className="form-control"
                                disabled={!isEditable}>
                                {serviceFocuses.map((option) => (
                                    <option key={option.id} value={option.id}>
                                        {option.name}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>
                        {locationFieldBadge('PracticeFocusType')}
                    </div>
                </td>
                <td hidden={!props.isOtherPracticeFocusColumnVisible}>
                    <FormGroup>
                        <Input
                            hidden={
                                !isOtherPracticeFocusInputVisible(
                                    `${props.fieldPrefix}[${props.index}].practiceFocusType`
                                )
                            }
                            name={`${props.fieldPrefix}[${props.index}].otherPracticeType`}
                            type="text"
                            className="form-control"
                            disabled={!isEditable}
                        />
                    </FormGroup>
                </td>
                {canEditRatings &&
                    RATING_PLATFORMS.map((platform) => (
                        <React.Fragment key={platform.key}>
                            <td className="location-rating-cell-input">
                                <div className="cell-with-badge">
                                    <FormGroup>
                                        <Input
                                            name={`${ratingsFieldPrefix}.${platform.key}`}
                                            type="number"
                                            step="0.1"
                                            min={0}
                                            max={5}
                                            className="form-control"
                                            value={formatRatingDisplay(
                                                getIn(
                                                    values,
                                                    `${ratingsFieldPrefix}.${platform.key}`
                                                )
                                            )}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                props.setFormFieldValue(
                                                    `${ratingsFieldPrefix}.${platform.key}`,
                                                    e.target.value
                                                )
                                            }
                                            onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                                                props.setFormFieldValue(
                                                    `${ratingsFieldPrefix}.${platform.key}`,
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </FormGroup>
                                    {locationFieldBadge(
                                        `Ratings.${platform.label}`
                                    )}
                                    {getIn(
                                        errors,
                                        `${ratingsFieldPrefix}.${platform.key}`
                                    ) && (
                                        <div className="invalid-feedback d-block">
                                            {getIn(
                                                errors,
                                                `${ratingsFieldPrefix}.${platform.key}`
                                            )}
                                        </div>
                                    )}
                                </div>
                            </td>
                            <td className="location-rating-cell-input">
                                <div className="cell-with-badge">
                                    <FormGroup>
                                        <Input
                                            name={`${ratingsFieldPrefix}.${platform.key}ReviewCount`}
                                            type="number"
                                            step="1"
                                            min={0}
                                            className="form-control"
                                            value={
                                                getIn(
                                                    values,
                                                    `${ratingsFieldPrefix}.${platform.key}ReviewCount`
                                                ) ?? ''
                                            }
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                props.setFormFieldValue(
                                                    `${ratingsFieldPrefix}.${platform.key}ReviewCount`,
                                                    e.target.value
                                                )
                                            }
                                            onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                                                props.setFormFieldValue(
                                                    `${ratingsFieldPrefix}.${platform.key}ReviewCount`,
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </FormGroup>
                                    {locationFieldBadge(
                                        `Ratings.${platform.label}ReviewCount`
                                    )}
                                </div>
                            </td>
                        </React.Fragment>
                    ))}
                {showReadonlyRatings &&
                    RATING_PLATFORMS.map((platform) => {
                        const ratingValue = getIn(
                            values,
                            `${ratingsFieldPrefix}.${platform.key}`
                        );
                        const reviewCount = getIn(
                            values,
                            `${ratingsFieldPrefix}.${platform.key}ReviewCount`
                        );
                        return (
                            <td
                                key={platform.key}
                                className="location-rating-cell-readonly">
                                {ratingValue != null && (
                                    <div className="cell-with-badge cell-with-badge--rating">
                                        <span className="location-rating-stack">
                                            <span className="location-rating-stack__stars">
                                                <StarRating
                                                    value={ratingValue}
                                                    size="sm"
                                                />
                                                {locationFieldBadge(
                                                    `Ratings.${platform.label}`,
                                                    true
                                                )}
                                            </span>
                                            {reviewCount != null &&
                                                reviewCount > 0 && (
                                                    <span className="location-rating-stack__count">
                                                        ({reviewCount})
                                                    </span>
                                                )}
                                        </span>
                                    </div>
                                )}
                            </td>
                        );
                    })}
                <td className="location-number">
                    <FontAwesomeIcon
                        size="lg"
                        icon={faTrash}
                        className={
                            isEditable ? 'delete-icon' : 'delete-icon disabled'
                        }
                        onClick={() =>
                            setIsDeleteConfirmationPopupVisible(true)
                        }
                    />
                </td>
            </tr>
            {isDeleteConfirmationPopupVisible && (
                <ConfirmationModal
                    isVisible={true}
                    setIsVisible={setIsDeleteConfirmationPopupVisible}
                    header={'Location Removal'}
                    confirmationText={`Are you sure you want to remove ${location.locationName}?`}
                    onConfirm={handleLocationRemoval}
                    nextButtonText={'Remove'}
                    cancelButtonText={'Cancel'}
                    params={null}
                />
            )}
        </>
    );
};
export default MarketplacePracticeLocation;
